import { createContext, useContext, useReducer, useEffect, useCallback, type ReactNode } from 'react';
import type { User, Team, Board, Task, Sprint, Label, Page, ViewType, TaskStatus, Comment, Attachment, SubTask, BoardColumn } from '../types';
import * as api from '../services/api';

interface AppState {
  currentUser: User;
  users: User[];
  teams: Team[];
  boards: Board[];
  tasks: Task[];
  sprints: Sprint[];
  labels: Label[];
  currentPage: Page;
  currentBoardId: string;
  currentView: ViewType;
  selectedTaskId: string | null;
  sidebarOpen: boolean;
  searchQuery: string;
  loading: boolean;
}

type Action =
  | { type: 'LOAD_DATA'; payload: { users: User[]; teams: Team[]; boards: Board[]; tasks: Task[]; sprints: Sprint[]; labels: Label[] } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PAGE'; payload: Page }
  | { type: 'SET_BOARD'; payload: string }
  | { type: 'SET_VIEW'; payload: ViewType }
  | { type: 'SELECT_TASK'; payload: string | null }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_OPEN'; payload: boolean }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'UPDATE_TASK'; payload: Task }
  | { type: 'ADD_TASK'; payload: Task }
  | { type: 'DELETE_TASK'; payload: string }
  | { type: 'MOVE_TASK'; payload: { taskId: string; status: TaskStatus } }
  | { type: 'ADD_COMMENT'; payload: { taskId: string; comment: Comment } }
  | { type: 'ADD_ATTACHMENT'; payload: { taskId: string; attachment: Attachment } }
  | { type: 'REMOVE_ATTACHMENT'; payload: { taskId: string; attachmentId: string } }
  | { type: 'UPDATE_SPRINT'; payload: Sprint }
  | { type: 'ADD_SPRINT'; payload: Sprint }
  | { type: 'DELETE_SPRINT'; payload: string }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'ADD_TEAM'; payload: Team }
  | { type: 'UPDATE_TEAM'; payload: Team }
  | { type: 'DELETE_TEAM'; payload: string }
  | { type: 'ADD_BOARD'; payload: Board }
  | { type: 'UPDATE_BOARD'; payload: Board }
  | { type: 'DELETE_BOARD'; payload: string }
  | { type: 'SET_LABELS'; payload: Label[] }
  | { type: 'SET_TASKS'; payload: Task[] }
  | { type: 'SET_BOARDS'; payload: Board[] }
  | { type: 'SET_TEAMS'; payload: Team[] }
  | { type: 'SET_SPRINTS'; payload: Sprint[] };

const emptyUser: User = {
  id: '',
  name: 'Carregando...',
  email: '',
  role: 'developer',
  teamIds: [],
  isOnline: false,
  avatarColor: '#3570E6',
};

const initialState: AppState = {
  currentUser: emptyUser,
  users: [],
  teams: [],
  boards: [],
  tasks: [],
  sprints: [],
  labels: [],
  currentPage: 'dashboard',
  currentBoardId: '',
  currentView: 'kanban',
  selectedTaskId: null,
  sidebarOpen: false,
  searchQuery: '',
  loading: true,
};

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'LOAD_DATA': {
      const { users, teams, boards, tasks, sprints, labels } = action.payload;
      return {
        ...state,
        users,
        teams,
        boards,
        tasks,
        sprints,
        labels,
        currentUser: users[0] || emptyUser,
        currentBoardId: boards[0]?.id || '',
        loading: false,
      };
    }
    case 'SET_LABELS':
      return { ...state, labels: action.payload };
    case 'SET_TASKS':
      return { ...state, tasks: action.payload };
    case 'SET_BOARDS':
      return { ...state, boards: action.payload };
    case 'SET_TEAMS':
      return { ...state, teams: action.payload };
    case 'SET_SPRINTS':
      return { ...state, sprints: action.payload };
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload, selectedTaskId: null };
    case 'SET_BOARD':
      return { ...state, currentBoardId: action.payload };
    case 'SET_VIEW':
      return { ...state, currentView: action.payload };
    case 'SELECT_TASK':
      return { ...state, selectedTaskId: action.payload };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    case 'SET_SIDEBAR_OPEN':
      return { ...state, sidebarOpen: action.payload };
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.payload };
    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: state.tasks.map((t) => (t.id === action.payload.id ? action.payload : t)),
      };
    case 'ADD_TASK':
      return { ...state, tasks: [...state.tasks, action.payload] };
    case 'DELETE_TASK':
      return {
        ...state,
        tasks: state.tasks.filter((t) => t.id !== action.payload),
        selectedTaskId: state.selectedTaskId === action.payload ? null : state.selectedTaskId,
      };
    case 'MOVE_TASK':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.taskId
            ? { ...t, status: action.payload.status, updatedAt: new Date().toISOString() }
            : t
        ),
      };
    case 'ADD_COMMENT':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.taskId
            ? { ...t, comments: [...t.comments, action.payload.comment], updatedAt: new Date().toISOString() }
            : t
        ),
      };
    case 'ADD_ATTACHMENT':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.taskId
            ? { ...t, attachments: [...t.attachments, action.payload.attachment], updatedAt: new Date().toISOString() }
            : t
        ),
      };
    case 'REMOVE_ATTACHMENT':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.taskId
            ? {
              ...t,
              attachments: t.attachments.filter((a) => a.id !== action.payload.attachmentId),
              updatedAt: new Date().toISOString(),
            }
            : t
        ),
      };
    case 'UPDATE_SPRINT':
      return {
        ...state,
        sprints: state.sprints.map((s) => (s.id === action.payload.id ? action.payload : s)),
      };
    case 'ADD_SPRINT':
      return { ...state, sprints: [...state.sprints, action.payload] };
    case 'DELETE_SPRINT':
      return {
        ...state,
        sprints: state.sprints.filter((s) => s.id !== action.payload),
        tasks: state.tasks.map((t) =>
          t.sprintId === action.payload ? { ...t, sprintId: null } : t
        ),
      };
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map((u) => (u.id === action.payload.id ? action.payload : u)),
        currentUser: state.currentUser.id === action.payload.id ? action.payload : state.currentUser,
      };
    case 'ADD_TEAM':
      return { ...state, teams: [...state.teams, action.payload] };
    case 'UPDATE_TEAM':
      return {
        ...state,
        teams: state.teams.map((t) => (t.id === action.payload.id ? action.payload : t)),
      };
    case 'DELETE_TEAM':
      return {
        ...state,
        teams: state.teams.filter((t) => t.id !== action.payload),
      };
    case 'ADD_BOARD':
      return { ...state, boards: [...state.boards, action.payload] };
    case 'UPDATE_BOARD':
      return {
        ...state,
        boards: state.boards.map((b) => (b.id === action.payload.id ? action.payload : b)),
      };
    case 'DELETE_BOARD':
      return {
        ...state,
        boards: state.boards.filter((b) => b.id !== action.payload),
        currentBoardId: state.currentBoardId === action.payload
          ? (state.boards.find((b) => b.id !== action.payload)?.id ?? '')
          : state.currentBoardId,
      };
    default:
      return state;
  }
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<Action>;
  getUserById: (id: string) => User | undefined;
  getTeamById: (id: string) => Team | undefined;
  getBoardById: (id: string) => Board | undefined;
  getLabelById: (id: string) => Label | undefined;
  getTasksForBoard: (boardId: string) => Task[];
  getTasksForSprint: (sprintId: string) => Task[];
  // API-backed async actions
  apiCreateTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Task | null>;
  apiUpdateTask: (task: Partial<Task> & { id: string }) => Promise<Task | null>;
  apiDeleteTask: (id: string) => Promise<boolean>;
  apiMoveTask: (taskId: string, status: TaskStatus) => Promise<boolean>;
  apiAddComment: (taskId: string, content: string, attachments?: { name: string; size: number; type: string; url: string }[]) => Promise<Comment | null>;
  apiAddAttachment: (taskId: string, att: { name: string; size: number; type: string; url: string }) => Promise<Attachment | null>;
  apiRemoveAttachment: (taskId: string, attachmentId: string) => Promise<boolean>;
  apiAddSubtask: (taskId: string, subtask: Omit<SubTask, 'id'>) => Promise<SubTask | null>;
  apiUpdateSubtask: (subtask: Partial<SubTask> & { id: string }) => Promise<boolean>;
  apiDeleteSubtask: (id: string) => Promise<boolean>;
  apiCreateBoard: (board: Omit<Board, 'id' | 'createdAt'>) => Promise<Board | null>;
  apiUpdateBoard: (board: Partial<Board> & { id: string }) => Promise<Board | null>;
  apiUpdateBoardColumns: (boardId: string, columns: BoardColumn[]) => Promise<Board | null>;
  apiDeleteBoard: (id: string) => Promise<boolean>;
  apiCreateTeam: (team: Omit<Team, 'id' | 'createdAt'>) => Promise<Team | null>;
  apiUpdateTeam: (team: Team) => Promise<Team | null>;
  apiDeleteTeam: (id: string) => Promise<boolean>;
  apiCreateSprint: (sprint: Omit<Sprint, 'id'>) => Promise<Sprint | null>;
  apiDeleteSprint: (id: string) => Promise<boolean>;
  apiCreateLabel: (label: Omit<Label, 'id'>) => Promise<Label | null>;
  refreshData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Load all data from API on mount
  const loadData = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const [usersData, teamsData, boardsData, tasksData, sprintsData, labelsData] = await Promise.all([
        api.fetchUsers(),
        api.fetchTeams(),
        api.fetchBoards(),
        api.fetchTasks(),
        api.fetchSprints(),
        api.fetchLabels(),
      ]);
      dispatch({
        type: 'LOAD_DATA',
        payload: {
          users: usersData,
          teams: teamsData,
          boards: boardsData,
          tasks: tasksData,
          sprints: sprintsData,
          labels: labelsData,
        },
      });
    } catch (err) {
      console.error('Failed to load data from API:', err);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getUserById = (id: string) => state.users.find((u) => u.id === id);
  const getTeamById = (id: string) => state.teams.find((t) => t.id === id);
  const getBoardById = (id: string) => state.boards.find((b) => b.id === id);
  const getLabelById = (id: string) => state.labels.find((l) => l.id === id);

  const getTasksForBoard = (boardId: string) => {
    const filtered = state.tasks.filter((t) => t.boardId === boardId);
    if (!state.searchQuery) return filtered;
    const q = state.searchQuery.toLowerCase();
    return filtered.filter(
      (t) => t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)
    );
  };

  const getTasksForSprint = (sprintId: string) =>
    state.tasks.filter((t) => t.sprintId === sprintId);

  // =====================================================================
  // API-backed async actions
  // =====================================================================

  const apiCreateTask = async (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<Task | null> => {
    try {
      const created = await api.createTask(task);
      dispatch({ type: 'ADD_TASK', payload: created });
      return created;
    } catch (e) { console.error(e); return null; }
  };

  const apiUpdateTask = async (task: Partial<Task> & { id: string }): Promise<Task | null> => {
    try {
      const updated = await api.updateTask(task);
      dispatch({ type: 'UPDATE_TASK', payload: updated });
      return updated;
    } catch (e) { console.error(e); return null; }
  };

  const apiDeleteTask = async (id: string): Promise<boolean> => {
    try {
      await api.deleteTask(id);
      dispatch({ type: 'DELETE_TASK', payload: id });
      return true;
    } catch (e) { console.error(e); return false; }
  };

  const apiMoveTask = async (taskId: string, status: TaskStatus): Promise<boolean> => {
    try {
      await api.moveTask(taskId, status);
      dispatch({ type: 'MOVE_TASK', payload: { taskId, status } });
      return true;
    } catch (e) { console.error(e); return false; }
  };

  const apiAddComment = async (taskId: string, content: string, attachments?: { name: string; size: number; type: string; url: string }[]): Promise<Comment | null> => {
    try {
      const comment = await api.addComment(taskId, content, attachments);
      dispatch({ type: 'ADD_COMMENT', payload: { taskId, comment } });
      return comment;
    } catch (e) { console.error(e); return null; }
  };

  const apiAddAttachment = async (taskId: string, att: { name: string; size: number; type: string; url: string }): Promise<Attachment | null> => {
    try {
      const attachment = await api.addAttachment(taskId, att);
      dispatch({ type: 'ADD_ATTACHMENT', payload: { taskId, attachment } });
      return attachment;
    } catch (e) { console.error(e); return null; }
  };

  const apiRemoveAttachment = async (taskId: string, attachmentId: string): Promise<boolean> => {
    try {
      await api.removeAttachment(attachmentId);
      dispatch({ type: 'REMOVE_ATTACHMENT', payload: { taskId, attachmentId } });
      return true;
    } catch (e) { console.error(e); return false; }
  };

  const apiAddSubtask = async (taskId: string, subtask: Omit<SubTask, 'id'>): Promise<SubTask | null> => {
    try {
      const created = await api.addSubtask(taskId, subtask);
      return created;
    } catch (e) { console.error(e); return null; }
  };

  const apiUpdateSubtask = async (subtask: Partial<SubTask> & { id: string }): Promise<boolean> => {
    try {
      await api.updateSubtask(subtask);
      return true;
    } catch (e) { console.error(e); return false; }
  };

  const apiDeleteSubtask = async (id: string): Promise<boolean> => {
    try {
      await api.deleteSubtask(id);
      return true;
    } catch (e) { console.error(e); return false; }
  };

  const apiCreateBoard = async (board: Omit<Board, 'id' | 'createdAt'>): Promise<Board | null> => {
    try {
      const created = await api.createBoard(board);
      dispatch({ type: 'ADD_BOARD', payload: created });
      return created;
    } catch (e) { console.error(e); return null; }
  };

  const apiUpdateBoard = async (board: Partial<Board> & { id: string }): Promise<Board | null> => {
    try {
      const updated = await api.updateBoard(board);
      dispatch({ type: 'UPDATE_BOARD', payload: updated });
      return updated;
    } catch (e) { console.error(e); return null; }
  };

  const apiUpdateBoardColumns = async (boardId: string, columns: BoardColumn[]): Promise<Board | null> => {
    try {
      const updated = await api.updateBoardColumns(boardId, columns);
      dispatch({ type: 'UPDATE_BOARD', payload: updated });
      return updated;
    } catch (e) { console.error(e); return null; }
  };

  const apiDeleteBoard = async (id: string): Promise<boolean> => {
    try {
      await api.deleteBoard(id);
      dispatch({ type: 'DELETE_BOARD', payload: id });
      return true;
    } catch (e) { console.error(e); return false; }
  };

  const apiCreateTeam = async (team: Omit<Team, 'id' | 'createdAt'>): Promise<Team | null> => {
    try {
      const created = await api.createTeam(team);
      dispatch({ type: 'ADD_TEAM', payload: created });
      return created;
    } catch (e) { console.error(e); return null; }
  };

  const apiUpdateTeam = async (team: Team): Promise<Team | null> => {
    try {
      const updated = await api.updateTeam(team);
      dispatch({ type: 'UPDATE_TEAM', payload: updated });
      return updated;
    } catch (e) { console.error(e); return null; }
  };

  const apiDeleteTeam = async (id: string): Promise<boolean> => {
    try {
      await api.deleteTeam(id);
      dispatch({ type: 'DELETE_TEAM', payload: id });
      return true;
    } catch (e) { console.error(e); return false; }
  };

  const apiCreateSprint = async (sprint: Omit<Sprint, 'id'>): Promise<Sprint | null> => {
    try {
      const created = await api.createSprint(sprint);
      dispatch({ type: 'ADD_SPRINT', payload: created });
      return created;
    } catch (e) { console.error(e); return null; }
  };

  const apiDeleteSprint = async (id: string): Promise<boolean> => {
    try {
      await api.deleteSprint(id);
      dispatch({ type: 'DELETE_SPRINT', payload: id });
      return true;
    } catch (e) { console.error(e); return false; }
  };

  const apiCreateLabel = async (label: Omit<Label, 'id'>): Promise<Label | null> => {
    try {
      const created = await api.createLabel(label);
      dispatch({ type: 'SET_LABELS', payload: [...state.labels, created] });
      return created;
    } catch (e) { console.error(e); return null; }
  };

  return (
    <AppContext.Provider
      value={{
        state, dispatch, getUserById, getTeamById, getBoardById, getLabelById,
        getTasksForBoard, getTasksForSprint,
        apiCreateTask, apiUpdateTask, apiDeleteTask, apiMoveTask,
        apiAddComment, apiAddAttachment, apiRemoveAttachment,
        apiAddSubtask, apiUpdateSubtask, apiDeleteSubtask,
        apiCreateBoard, apiUpdateBoard, apiUpdateBoardColumns, apiDeleteBoard,
        apiCreateTeam, apiUpdateTeam, apiDeleteTeam,
        apiCreateSprint, apiDeleteSprint, apiCreateLabel,
        refreshData: loadData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
